import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Clock,
  Navigation,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  Settings,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getCurrentCoords } from "../lib/geo";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";

/**
 * Modo Kiosk — versão ultra-simplificada do app do colaborador.
 *
 * Mostra UM job de cada vez (o mais próximo/em andamento) com:
 *  - Nome do cliente + endereço gigante
 *  - Código da porta destacado
 *  - 1 botão de ação:
 *      - status=agendada/deslocamento → "CHEGUEI NA CASA" (valida GPS, vira andamento)
 *      - status=andamento             → "FINALIZAR LIMPEZA" (abre tela de detalhe)
 *  - Botão pra ir ao próximo job depois de finalizar
 *
 * Sem cards, sem contatos, sem calendário, sem listas. Foco total no que fazer agora.
 */
export default function MobileKiosk() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [job, setJob] = useState(null);
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadNext = useCallback(async () => {
    setLoading(true);
    try {
      const { data: jobs } = await api.get("/jobs");
      const candidates = jobs
        .filter((j) => ["agendada", "deslocamento", "andamento"].includes(j.status))
        .sort((a, b) => {
          const order = { andamento: 0, deslocamento: 1, agendada: 2 };
          if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
          return new Date(a.scheduled_at) - new Date(b.scheduled_at);
        });
      // Tenta candidatos até achar um com cliente válido (pula jobs com cliente deletado).
      let picked = null;
      let pickedClient = null;
      for (const cand of candidates) {
        try {
          const { data: full } = await api.get(`/jobs/${cand.id}`);
          if (full.client) {
            picked = full;
            pickedClient = full.client;
            break;
          }
        } catch {
          // Pula esse candidato e tenta o próximo
        }
      }
      setJob(picked);
      setClient(pickedClient);
    } catch {
      toast.error(t("Falha ao carregar trabalho"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) await loadNext();
    })();
    return () => {
      mounted = false;
    };
  }, [loadNext]);

  async function getCoords() {
    return getCurrentCoords();
  }

  async function arrived() {
    if (!job) return;
    setBusy(true);
    try {
      const { lat, lng } = await getCoords();
      await api.post(`/jobs/${job.id}/start`, { lat, lng });
      toast.success(t("Trabalho iniciado"));
      await loadNext();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || t("Falha ao iniciar");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  function openFinish() {
    if (!job) return;
    nav(`/mobile/job/${job.id}?finish=1`);
  }

  function openMaps() {
    if (!client) return;
    const addr = [client.address, client.city, client.state, client.zip_code]
      .filter(Boolean)
      .join(", ");
    window.open(
      `https://maps.google.com/?q=${encodeURIComponent(addr)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  const isLive = job?.status === "andamento";
  const fullAddress = client
    ? [client.address + (client.apt_number ? ` · Apt ${client.apt_number}` : ""), client.city, client.state, client.zip_code]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div
      className="min-h-screen bg-slate-900 text-white flex flex-col"
      data-testid="mobile-kiosk"
    >
      {/* Top bar (mini) */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button
          onClick={() => nav("/mobile")}
          data-testid="kiosk-exit"
          className="text-sm font-semibold text-slate-300 hover:text-white flex items-center gap-1.5"
        >
          <ChevronLeft size={18} /> {t("Sair do modo Foco")}
        </button>
        <div className="text-[11px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
          <Settings size={12} /> {t("Foco")}
        </div>
      </div>

      <div className="flex-1 flex flex-col px-6 py-8">
        {loading && (
          <div className="m-auto text-center">
            <Loader2 size={32} className="animate-spin text-blue-400 mx-auto mb-3" />
            <div className="text-slate-400 text-sm">{t("Carregando…")}</div>
          </div>
        )}

        {!loading && !job && (
          <div className="m-auto text-center max-w-xs">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} />
            </div>
            <div className="text-2xl font-bold">{t("Tudo certo!")}</div>
            <div className="text-slate-400 mt-2">
              {t("Você não tem nenhuma limpeza pendente no momento.")}
            </div>
            <button
              onClick={loadNext}
              data-testid="kiosk-refresh"
              className="mt-6 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-sm font-bold"
            >
              {t("Atualizar")}
            </button>
          </div>
        )}

        {!loading && job && client && (
          <>
            {/* Status pill */}
            <div className="flex items-center justify-center mb-6">
              <span
                className={`px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest ${
                  isLive
                    ? "bg-emerald-500 text-white"
                    : "bg-blue-500 text-white"
                }`}
                data-testid="kiosk-status"
              >
                {isLive ? t("● Em andamento") : t("Próximo trabalho")}
              </span>
            </div>

            {/* Property name (huge) */}
            <div
              className="text-center text-4xl font-extrabold leading-tight mb-3 break-words"
              data-testid="kiosk-client-name"
            >
              {client.name}
            </div>

            {/* Time */}
            <div className="flex items-center justify-center gap-2 text-slate-300 text-lg mb-6">
              <Clock size={18} />
              <span>
                {new Date(job.scheduled_at).toLocaleString("en-US", {
                  weekday: "long",
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            {/* Address */}
            <button
              onClick={openMaps}
              data-testid="kiosk-address"
              className="block w-full mb-5 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-left transition"
            >
              <div className="flex items-start gap-3">
                <MapPin size={22} className="text-blue-400 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-base leading-snug">{fullAddress}</div>
                  <div className="text-[11px] text-blue-300 mt-1 font-semibold">
                    {t("Tocar para abrir no Google Maps →")}
                  </div>
                </div>
              </div>
            </button>

            {/* Door code (huge if exists) */}
            {client.door_code && (
              <div
                className="mb-5 p-5 rounded-2xl bg-amber-500/15 border border-amber-400/30"
                data-testid="kiosk-door-code"
              >
                <div className="text-[11px] uppercase tracking-widest text-amber-300 mb-1">
                  {t("🔑 Código da porta")}
                </div>
                <div className="text-4xl font-mono font-bold tracking-widest text-amber-100">
                  {client.door_code}
                </div>
              </div>
            )}

            {/* Observations (warning) */}
            {client.observations && (
              <div
                className="mb-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900"
                data-testid="kiosk-observations"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <div className="text-sm whitespace-pre-line">
                    {client.observations}
                  </div>
                </div>
              </div>
            )}

            {/* Job notes */}
            {job.notes && (
              <div className="mb-5 p-3 rounded-xl bg-white/5 text-slate-200 text-sm whitespace-pre-line">
                {job.notes}
              </div>
            )}

            <div className="flex-1" />

            {/* HUGE primary action */}
            {!isLive ? (
              <button
                type="button"
                onClick={arrived}
                disabled={busy}
                data-testid="kiosk-cheguei"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-extrabold py-6 rounded-2xl shadow-2xl shadow-blue-500/40 text-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition"
              >
                {busy ? (
                  <>
                    <Loader2 size={28} className="animate-spin" /> {t("Validando GPS…")}
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={28} /> {t("CHEGUEI NA CASA")}
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={openFinish}
                data-testid="kiosk-finalizar"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-6 rounded-2xl shadow-2xl shadow-emerald-500/40 text-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition"
              >
                <CheckCircle2 size={28} /> {t("FINALIZAR LIMPEZA")}
              </button>
            )}

            {!isLive && (
              <button
                type="button"
                onClick={openMaps}
                className="mt-3 w-full bg-white/5 hover:bg-white/10 text-slate-200 font-semibold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
              >
                <Navigation size={16} /> {t("Abrir rota no mapa")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
